import { useMemo } from 'react';
import { typography, radius } from '@/shared/theme/tokens';
import { LIVE_STATUS_META, computeStatus } from '../../liveModeMeta';
import { Sparkline } from '../../ui/Sparkline';
import { MONO } from './recommendedTheme';

const r1 = (n) => Math.round(n * 10) / 10;

function inBandPct(alerts) {
  let total = 0; let inBand = 0;
  for (const a of alerts) for (const v of a.buffer ?? []) { total += 1; if (computeStatus(v, a) === 'ok') inBand += 1; }
  return total ? Math.round((inBand / total) * 100) : null;
}

function pickMetric(alerts) {
  const byName = new Map();
  for (const a of alerts) if (!byName.has(a.parameterName)) byName.set(a.parameterName, a);
  const all = [...byName.values()];
  return all.find((a) => a.paramType === 'target') ?? all[0] ?? null;
}

/**
 * Session readout (the Terminal/Consensus handoff strip) — "how the plant sat
 * this window". Session-derived from the live buffers, labelled "Last 12h".
 * Theme-aware.
 */
export function SessionStrip({ alerts = [], recs = [], c }) {
  const { pct, activeAlerts, awaiting, metric } = useMemo(() => ({
    pct: inBandPct(alerts),
    activeAlerts: alerts.filter((a) => !a.isStale && a.status !== 'ok').length,
    awaiting: recs.filter((r) => r.status === 'accepted').length,
    metric: pickMetric(alerts),
  }), [alerts, recs]);

  const peak = metric?.buffer?.length ? r1(Math.max(...metric.buffer)) : metric?.liveValue;
  const meta = metric ? (LIVE_STATUS_META[metric.status] ?? LIVE_STATUS_META.ok) : LIVE_STATUS_META.ok;

  return (
    <section style={{
      flexShrink: 0, background: c.bg, border: `1px solid ${c.border}`, borderRadius: radius.xl, overflow: 'hidden',
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '9px 14px', borderBottom: `1px solid ${c.border}` }}>
        <span style={{ fontFamily: typography.fontFamily.heading, fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.bold, color: c.textPrimary, letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase' }}>
          Session
        </span>
        <span style={{ fontFamily: MONO, fontSize: typography.fontSize.xs, color: c.textSubtle, letterSpacing: typography.letterSpacing.wide }}>LAST 12H</span>
      </div>
      <div style={{ display: 'flex' }}>
        <Readout c={c} label="In band" value={pct == null ? '—' : `${pct}%`} />
        <Readout c={c} label="Active" value={activeAlerts} divider />
        <Readout c={c} label="Awaiting" value={awaiting} divider />
      </div>
      {metric && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderTop: `1px solid ${c.border}` }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontFamily: MONO, fontSize: typography.fontSize.xs, color: c.textMuted, letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {metric.parameterName}
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
              <span style={{ fontFamily: MONO, fontSize: typography.fontSize['2xl'], fontWeight: typography.fontWeight.bold, color: c.textPrimary, fontVariantNumeric: 'tabular-nums' }}>{peak}</span>
              {metric.unit && <span style={{ fontFamily: MONO, fontSize: typography.fontSize.xs, color: c.textSubtle }}>{metric.unit}</span>}
              <span style={{ fontFamily: MONO, fontSize: typography.fontSize.xs, color: meta.color, marginLeft: 2 }}>PEAK</span>
            </div>
          </div>
          <div style={{ width: 96, height: 26 }}>
            <Sparkline data={metric.buffer} color={meta.color} height={26} animate={false} />
          </div>
        </div>
      )}
    </section>
  );
}

function Readout({ label, value, divider, c }) {
  return (
    <div style={{ flex: 1, padding: '10px 14px', borderLeft: divider ? `1px solid ${c.border}` : 'none' }}>
      <div style={{ fontFamily: MONO, fontSize: typography.fontSize['3xl'], fontWeight: typography.fontWeight.bold, color: c.textPrimary, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>
        {value}
      </div>
      <div style={{ fontFamily: MONO, fontSize: typography.fontSize.xs, color: c.textSubtle, letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase', marginTop: 2 }}>
        {label}
      </div>
    </div>
  );
}
